// The valid tenant content-bundle ids (the `content.bundles` back-office setting's
// `bundlesAvailable` values). This module is the canonical source of truth; sj-admin's
// tenant-management picker imports these values/labels from here.
export type ContentBundle = "ai_access" | "ai_adv" | "enterprise" | "eu_ai_act";

export const CONTENT_BUNDLES = {
  AI_ACCESS: "ai_access",
  AI_ADVANTAGE: "ai_adv",
  ENTERPRISE: "enterprise",
  EU_AI_ACT: "eu_ai_act",
} as const satisfies Record<string, ContentBundle>;

// Human-readable display names for each bundle (e.g. the sj-admin tenant-management picker).
// Typed as Record<ContentBundle, string> so every bundle must have a label — add one here when
// you add a bundle to CONTENT_BUNDLES.
export const CONTENT_BUNDLE_LABELS: Record<ContentBundle, string> = {
  [CONTENT_BUNDLES.AI_ACCESS]: "AI Access",
  [CONTENT_BUNDLES.AI_ADVANTAGE]: "AI Advantage",
  [CONTENT_BUNDLES.ENTERPRISE]: "Enterprise",
  [CONTENT_BUNDLES.EU_AI_ACT]: "EU AI Act",
};

// All valid bundle ids (e.g. to validate a persisted `content.bundles.bundlesAvailable` list).
export const CONTENT_BUNDLE_VALUES = Object.keys(CONTENT_BUNDLE_LABELS) as ContentBundle[];

// { label, value } options for bundle pickers (e.g. the sj-admin tenant-management MultiSelect).
export const CONTENT_BUNDLE_OPTIONS: { label: string; value: ContentBundle }[] =
  CONTENT_BUNDLE_VALUES.map(value => ({ label: CONTENT_BUNDLE_LABELS[value], value }));

// Bundles that mark a tenant as an AI tenant (vs enterprise / none).
export const AI_BUNDLES: ContentBundle[] = [
  CONTENT_BUNDLES.AI_ACCESS,
  CONTENT_BUNDLES.AI_ADVANTAGE,
  CONTENT_BUNDLES.EU_AI_ACT,
];

// True when the tenant has any AI bundle available.
export const hasAiBundle = (bundles: readonly string[] | null | undefined): boolean =>
  (bundles ?? []).some(bundle => AI_BUNDLES.includes(bundle as ContentBundle));

export const isOnlyAiAccessBundle = (bundles: readonly string[] | null | undefined): boolean =>
  Array.isArray(bundles) && bundles.length === 1 && bundles[0] === CONTENT_BUNDLES.AI_ACCESS;

// True when the tenant has the EU AI Act bundle available (membership test, so it also
// holds if the tenant ever carries eu_ai_act alongside another bundle).
export const hasEuAiActBundle = (bundles: readonly string[] | null | undefined): boolean =>
  (bundles ?? []).includes(CONTENT_BUNDLES.EU_AI_ACT);
